import { useEffect, useRef, useState } from "react";
import { ReactSortable, Sortable, Store } from "react-sortablejs";
import styled, { useTheme } from "styled-components";
import Icon from "../../../components/Icon/Icon";
import { sortableOptions } from "../../../components/Draggable/Draggable";
import { useAlert } from "../../../components/Alert/Alerts";

export type ThemisRuleListItem = {
  id: number;
  name: string;
  targetScopeKey: string;
  phase: string;
  priority: number;
  isEnabled: boolean;
  stopProcessing: boolean;
};

export const StyledRuleCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundLight};
  padding: 0.5rem;
  padding-left: calc(0.5rem + 2px);
  border: solid 1px ${({ theme }) => theme.colorBorderLight};
  cursor: pointer;

  .rule-display {
    display: flex;
    align-items: center;
    min-height: 24px;
    margin-right: auto;
  }
`;

type Props = {
  rules: ThemisRuleListItem[];
  onEdit(id: number): void; // navigate
  onDelete(id: number): void;
  onReorder(orderedRuleIds: number[]): Promise<void>;
};

export default function DraggableThemisRulesList({
  rules,
  onEdit,
  onDelete,
  onReorder,
}: Props) {
  const { addNewAlert } = useAlert();
  const ref = useRef<ThemisRuleListItem[] | null>(null);
  const [list, setList] = useState<ThemisRuleListItem[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  useEffect(() => {
    ref.current = rules;
    setList(rules);
  }, [rules]);

  const handleReorder = async (
    newOrder: ThemisRuleListItem[],
    _sortable: Sortable | null,
    store: Store,
  ) => {
    if (!store.dragging) return;

    const prev = ref.current ?? [];
    if (prev.length !== newOrder.length) return;

    // Prevent duplicate calls while the sortable is still mutating
    if (
      JSON.stringify(prev.map((x) => x.id)) ===
      JSON.stringify(newOrder.map((x) => x.id))
    ) {
      ref.current = newOrder;
      return;
    }

    setList(newOrder);
    ref.current = newOrder;

    if (isSavingOrder) return;

    setIsSavingOrder(true);
    try {
      await onReorder(newOrder.map((x) => x.id));
      addNewAlert({ type: "success", message: "Successfully updated order." });
    } catch {
      addNewAlert({ type: "error", message: "Failed to update order." });
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <ReactSortable
      list={list}
      setList={(newList, sortable, store) => {
        void handleReorder(newList, sortable, store);
      }}
      {...sortableOptions({
        group: "themis-rules",
        name: "themis-rules",
        drop: false,
        sortable: true,
      })}
    >
      {list.length > 0 ? (
        list.map((rule) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      ) : (
        <p>No rules found</p>
      )}
    </ReactSortable>
  );
}

function RuleCard({
  rule,
  onEdit,
  onDelete,
}: {
  rule: ThemisRuleListItem;
  onEdit(id: number): void;
  onDelete(id: number): void;
}) {
  const theme = useTheme();

  return (
    <StyledRuleCard className="draggable">
      <p className="rule-display">{rule.name}</p>

      <span
        className="self-center"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(rule.id);
        }}
      >
        <Icon
          icon="edit"
          color={theme.colorPrimary}
          width="18px"
          height="18px"
        />
      </span>

      <span
        className="self-center"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(rule.id);
        }}
      >
        <Icon
          icon="trash"
          color={theme.colorPrimary}
          width="18px"
          height="18px"
        />
      </span>
    </StyledRuleCard>
  );
}
